import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Upload, Sparkles, Wand2, Send, Zap, Crop, Type, Edit3, Share2, Gauge,
  Play, Check, ChevronDown, Star, ArrowRight, TrendingUp,
} from 'lucide-react';
import { useT } from '@/lib/i18n';

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } };

const Section = ({ children, className = '', testid }) => (
  <section className={`py-20 sm:py-28 ${className}`} data-testid={testid}>
    <div className="max-w-7xl mx-auto px-6 lg:px-8">{children}</div>
  </section>
);

const SectionHeader = ({ label, title, subtitle }) => (
  <div className="max-w-3xl mx-auto text-center mb-16">
    {label && (
      <span className="inline-block text-xs uppercase tracking-[0.25em] text-[#D4FF00] font-mono mb-4">{label}</span>
    )}
    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-4">{title}</h2>
    {subtitle && <p className="text-zinc-400 text-base sm:text-lg leading-relaxed">{subtitle}</p>}
  </div>
);

/* ───────────────────────── SECTION 2: SOCIAL PROOF (platforms) ───────────────────────── */
export const SocialProof = () => {
  const { lang } = useT();
  const platforms = [
    { name: 'TikTok', icon: 'M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V7.05a8.16 8.16 0 0 0 4.77 1.52V5.16a4.85 4.85 0 0 1-1.84-.47z' },
    { name: 'Instagram', icon: 'M12 2.16c3.2 0 3.58 0 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.15-3.23 1.66-4.77 4.92-4.92C8.42 2.17 8.8 2.16 12 2.16zm0 5.84a3.84 3.84 0 1 0 0 7.68 3.84 3.84 0 0 0 0-7.68zm0 6.33a2.49 2.49 0 1 1 0-4.98 2.49 2.49 0 0 1 0 4.98zm4.96-6.5a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8z' },
    { name: 'YouTube', icon: 'M23.5 6.2a3 3 0 0 0-2.12-2.12C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.58A3 3 0 0 0 .5 6.2 31.2 31.2 0 0 0 0 12a31.2 31.2 0 0 0 .5 5.8 3 3 0 0 0 2.12 2.12C4.5 20.5 12 20.5 12 20.5s7.5 0 9.38-.58a3 3 0 0 0 2.12-2.12A31.2 31.2 0 0 0 24 12a31.2 31.2 0 0 0-.5-5.8zM9.6 15.6V8.4l6.4 3.6-6.4 3.6z' },
    { name: 'LinkedIn', icon: 'M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 18.34v-8H5.67v8h2.67zM7 9.11a1.56 1.56 0 1 0 0-3.11 1.56 1.56 0 0 0 0 3.11zm11.34 9.23v-4.39c0-2.34-1.25-3.43-2.93-3.43a2.53 2.53 0 0 0-2.3 1.27V10.34h-2.66v8h2.66v-4.43c0-1.16.22-2.28 1.66-2.28 1.42 0 1.44 1.32 1.44 2.36v4.35h2.13z' },
    { name: 'X', icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
  ];
  return (
    <Section className="border-t border-b border-white/5 bg-[#070707]" testid="landing-social-proof">
      <div className="text-center">
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-mono mb-8" data-testid="social-proof-label">
          {lang === 'fr' ? 'Prêt pour toutes les plateformes' : 'Ready for every platform'}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
          {platforms.map((p) => (
            <motion.div
              key={p.name}
              whileHover={{ scale: 1.05, color: '#D4FF00' }}
              className="flex items-center gap-2 text-zinc-500 hover:text-[#D4FF00] transition-colors"
              data-testid={`platform-${p.name.toLowerCase()}`}
            >
              <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor"><path d={p.icon} /></svg>
              <span className="font-heading font-bold text-lg hidden sm:inline">{p.name}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
};

/* ───────────────────────── SECTION 3: HOW IT WORKS ───────────────────────── */
export const HowItWorks = () => {
  const { lang } = useT();
  const steps = [
    { num: '01', icon: Upload, title: lang === 'fr' ? 'Uploadez' : 'Upload', desc: lang === 'fr' ? 'Déposez votre podcast, interview ou stream. Jusqu\'à 5 Go.' : 'Drop your podcast, interview or stream. Up to 5 GB.' },
    { num: '02', icon: Sparkles, title: lang === 'fr' ? 'L\'IA détecte' : 'AI detects', desc: lang === 'fr' ? 'Whisper transcrit. GPT note. Les moments viraux sont identifiés.' : 'Whisper transcribes. GPT scores. Viral moments get identified.' },
    { num: '03', icon: Edit3, title: lang === 'fr' ? 'Éditez si besoin' : 'Edit if needed', desc: lang === 'fr' ? 'Affinez les coupes, ajustez les sous-titres dans l\'éditeur intégré.' : 'Fine-tune cuts, adjust captions in the built-in editor.' },
    { num: '04', icon: Send, title: lang === 'fr' ? 'Publiez' : 'Publish', desc: lang === 'fr' ? 'Téléchargez ou partagez vos clips prêts pour TikTok, Reels, Shorts.' : 'Download or share your clips ready for TikTok, Reels, Shorts.' },
  ];
  return (
    <Section testid="landing-how-it-works">
      <div id="how-it-works" />
      <SectionHeader
        label={lang === 'fr' ? '// Workflow' : '// Workflow'}
        title={lang === 'fr' ? 'Quatre étapes. Zéro montage.' : 'Four steps. Zero editing.'}
        subtitle={lang === 'fr' ? 'De l\'upload à la publication, en moins de 5 minutes.' : 'From upload to publish, in under 5 minutes.'}
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {steps.map((s, i) => (
          <motion.div
            key={s.num}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.5 }}
            className="bento-card rounded-2xl glass-card p-6 relative"
            data-testid={`how-step-${i + 1}`}
          >
            <div className="flex items-center justify-between mb-6">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-600">{s.num}</span>
              <s.icon className="w-5 h-5 text-[#D4FF00]" />
            </div>
            <h3 className="font-heading text-xl font-bold mb-2">{s.title}</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

/* ───────────────────────── SECTION 4: BEFORE / AFTER ───────────────────────── */
export const BeforeAfter = () => {
  const { lang } = useT();
  return (
    <Section className="bg-[#070707]" testid="landing-before-after">
      <SectionHeader
        label={lang === 'fr' ? '// Transformation' : '// Transformation'}
        title={lang === 'fr' ? 'Avant. Après.' : 'Before. After.'}
        subtitle={lang === 'fr' ? 'D\'une vidéo horizontale d\'une heure à un clip vertical de 45 secondes prêt à viraliser.' : 'From a one-hour horizontal video to a 45-second vertical clip ready to go viral.'}
      />
      <div className="grid md:grid-cols-2 gap-6 items-center max-w-5xl mx-auto">
        {/* Before: horizontal YouTube */}
        <div className="space-y-3" data-testid="before-block">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono">
            <span>{lang === 'fr' ? 'AVANT · YouTube long' : 'BEFORE · YouTube long-form'}</span>
            <span className="text-zinc-600">58:24</span>
          </div>
          <div className="aspect-video rounded-xl glass-card relative overflow-hidden bento-card">
            <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 via-zinc-900 to-zinc-950" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-red-600/90 flex items-center justify-center">
                <Play className="w-7 h-7 text-white fill-white ml-1" />
              </div>
            </div>
            <div className="absolute bottom-3 left-3 right-3 h-1 bg-white/10 rounded-full">
              <div className="h-full w-1/3 bg-red-600 rounded-full" />
            </div>
          </div>
        </div>
        {/* Arrow + viral score */}
        <div className="flex md:flex-col items-center justify-center gap-4 md:order-3 md:col-span-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
            className="px-4 py-2 rounded-full acid-chip text-sm flex items-center gap-2"
            data-testid="viral-score-badge"
          >
            <TrendingUp className="w-4 h-4" />
            {lang === 'fr' ? 'Score viral · 94/100' : 'Viral score · 94/100'}
          </motion.div>
        </div>
        {/* After: vertical clip with captions */}
        <div className="space-y-3 mx-auto" data-testid="after-block">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono">
            <span>{lang === 'fr' ? 'APRÈS · TikTok ready' : 'AFTER · TikTok ready'}</span>
            <span className="text-[#D4FF00]">0:45</span>
          </div>
          <div className="w-56 aspect-[9/16] rounded-2xl glass-card relative overflow-hidden mx-auto">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-zinc-900 to-zinc-950" />
            <div className="absolute top-3 left-3 px-2 py-1 rounded acid-chip text-[10px]">94 VIRAL</div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center">
                <Play className="w-4 h-4 text-white fill-white ml-0.5" />
              </div>
            </div>
            <div className="absolute bottom-10 left-2 right-2 text-center">
              <div className="tiktok-caption">
                {lang === 'fr' ? <>VOICI LE <span className="hl">MOMENT</span><br />QUI <span className="hl">CHANGE</span> TOUT</> : <>THIS IS THE <span className="hl">MOMENT</span><br />THAT <span className="hl">CHANGES</span> EVERYTHING</>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

/* ───────────────────────── SECTION 5: FEATURES (bento) ───────────────────────── */
export const Features = () => {
  const { lang } = useT();
  const features = [
    { icon: Sparkles, title: lang === 'fr' ? 'Détection virale IA' : 'AI Viral Detection', desc: lang === 'fr' ? 'GPT-5.2 score chaque moment de 1 à 100 selon son potentiel viral.' : 'GPT-5.2 scores every moment 1-100 by viral potential.', span: 'lg:col-span-2 lg:row-span-2' },
    { icon: Crop, title: lang === 'fr' ? 'Recadrage 9:16' : 'Smart 9:16 Crop', desc: lang === 'fr' ? 'Suit le visage qui parle, jamais un cadrage raté.' : 'Tracks the speaker face, never miss the action.' },
    { icon: Type, title: lang === 'fr' ? 'Sous-titres animés' : 'Animated Captions', desc: lang === 'fr' ? 'Style CapCut, mot-à-mot, sync parfaite.' : 'CapCut style, word-by-word, perfect sync.' },
    { icon: Edit3, title: lang === 'fr' ? 'Éditeur intégré' : 'Built-in Editor', desc: lang === 'fr' ? 'Timeline, trim, retouche en un clic.' : 'Timeline, trim, fine-tune in one click.' },
    { icon: Share2, title: lang === 'fr' ? 'Pages de partage' : 'Share Pages', desc: lang === 'fr' ? 'Liens publics avec OG cards optimisées.' : 'Public links with optimized OG cards.' },
    { icon: Gauge, title: lang === 'fr' ? 'Ultra-rapide' : 'Lightning Fast', desc: lang === 'fr' ? '5 à 10 clips en moins de 5 minutes.' : '5 to 10 clips in under 5 minutes.' },
  ];
  return (
    <Section testid="landing-features">
      <SectionHeader
        label={lang === 'fr' ? '// Capacités' : '// Capabilities'}
        title={lang === 'fr' ? 'Construit pour scaler.' : 'Built for scale.'}
        subtitle={lang === 'fr' ? 'Chaque détail pensé pour transformer ton long-form en machine à viralité.' : 'Every detail engineered to turn your long-form into a virality machine.'}
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 gap-4 auto-rows-fr">
        {features.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.05, duration: 0.5 }}
            className={`bento-card rounded-2xl glass-card p-6 relative overflow-hidden ${f.span || ''}`}
            data-testid={`feature-${i + 1}`}
          >
            {f.span && (
              <div className="absolute top-0 right-0 w-48 h-48 bg-[#D4FF00]/5 rounded-full blur-3xl pointer-events-none" />
            )}
            <div className="w-10 h-10 rounded-lg bg-[#D4FF00]/10 border border-[#D4FF00]/20 flex items-center justify-center mb-4 relative">
              <f.icon className="w-5 h-5 text-[#D4FF00]" />
            </div>
            <h3 className="font-heading text-lg sm:text-xl font-bold mb-2">{f.title}</h3>
            <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

/* ───────────────────────── SECTION 6: SHOWCASE ───────────────────────── */
const SHOWCASE = [
  { thumb: 'https://images.unsplash.com/photo-1581368087049-7034ed0d1e6f?auto=format&fit=crop&w=400&q=60', duration: '0:42', score: 94, cat: 'Podcast' },
  { thumb: 'https://images.unsplash.com/photo-1654723011673-86b0eae447a8?auto=format&fit=crop&w=400&q=60', duration: '0:38', score: 89, cat: 'Interview' },
  { thumb: 'https://images.unsplash.com/photo-1581368135153-a506cf13b1e1?auto=format&fit=crop&w=400&q=60', duration: '0:55', score: 92, cat: 'Stream' },
  { thumb: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?auto=format&fit=crop&w=400&q=60', duration: '0:31', score: 87, cat: 'Vlog' },
];

export const Showcase = () => {
  const { lang } = useT();
  return (
    <Section className="bg-[#070707]" testid="landing-showcase">
      <SectionHeader
        label={lang === 'fr' ? '// Inspiration' : '// Showcase'}
        title={lang === 'fr' ? 'Des clips qui performent.' : 'Clips that perform.'}
        subtitle={lang === 'fr' ? 'Aperçu de ce que l\'IA peut générer à partir de tes longues vidéos.' : 'Preview of what the AI can generate from your long-form content.'}
      />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {SHOWCASE.map((c, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.08, duration: 0.5 }}
            whileHover={{ y: -4 }}
            className="relative aspect-[9/16] rounded-2xl overflow-hidden cursor-pointer group border border-white/10"
            data-testid={`showcase-clip-${i + 1}`}
          >
            <img src={c.thumb} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
              <span className="px-2 py-1 rounded acid-chip text-[10px]">{c.score} VIRAL</span>
              <span className="text-[10px] font-mono text-white bg-black/60 px-2 py-1 rounded">{c.duration}</span>
            </div>
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-12 h-12 rounded-full bg-[#D4FF00] flex items-center justify-center">
                <Play className="w-5 h-5 text-black fill-black ml-0.5" />
              </div>
            </div>
            <div className="absolute bottom-3 left-3 right-3">
              <p className="text-xs font-mono uppercase tracking-wider text-zinc-300">{c.cat}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

/* ───────────────────────── SECTION 7: BUILT FOR CREATORS (honnête, sans faux témoignages) ───────────────────────── */
const BuiltForItem = ({ icon: Icon, title, subtitle, testid }) => (
  <div className="p-6 rounded-2xl border border-white/8 bg-white/[0.02]" data-testid={testid}>
    <div className="w-10 h-10 rounded-lg bg-[#D4FF00]/10 border border-[#D4FF00]/25 flex items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-[#D4FF00]" />
    </div>
    <h3 className="font-heading text-lg font-bold tracking-tight mb-1.5">{title}</h3>
    <p className="text-zinc-400 text-sm leading-relaxed">{subtitle}</p>
  </div>
);

export const Testimonials = () => {
  const { lang } = useT();
  const items = [
    {
      icon: Sparkles,
      title: lang === 'fr' ? 'Construit pour les créateurs' : 'Built for creators',
      subtitle: lang === 'fr'
        ? 'Podcasteurs, streamers, coaches, éducateurs — le workflow HighlightAI est calibré pour chaque type de contenu, pas pour une seule niche.'
        : 'Podcasters, streamers, coaches, educators — the HighlightAI workflow is tuned for every content type, not a single niche.',
      testid: 'builtfor-creators',
    },
    {
      icon: TrendingUp,
      title: lang === 'fr' ? 'Optimisé pour TikTok, Shorts, Reels' : 'Optimised for TikTok, Shorts, Reels',
      subtitle: lang === 'fr'
        ? '9:16 par défaut, sous-titres CapCut-tier, hooks pensés pour les premières secondes — les formats verticaux sont la première classe.'
        : '9:16 by default, CapCut-tier captions, hooks engineered for the first seconds — vertical formats are first-class.',
      testid: 'builtfor-vertical',
    },
    {
      icon: Zap,
      title: lang === 'fr' ? 'Ta transparence, notre défaut' : 'Transparency, by default',
      subtitle: lang === 'fr'
        ? 'Aucun faux témoignage, aucun score gonflé. Ce que tu vois dans le Dashboard est ce que tes clips valent réellement.'
        : 'No fake testimonials, no inflated scores. What you see in the Dashboard is what your clips are actually worth.',
      testid: 'builtfor-transparent',
    },
  ];
  return (
    <Section testid="landing-testimonials">
      <SectionHeader
        label={lang === 'fr' ? '// Le produit' : '// The product'}
        title={lang === 'fr' ? 'Fait pour ceux qui publient sérieusement.' : 'Built for people who publish seriously.'}
        subtitle={lang === 'fr'
          ? 'Pas de fausse promesse. Pas de faux avis. Juste un outil qui fait ce qu\'il annonce.'
          : "No fake promise. No fake reviews. Just a tool that does exactly what it says."}
      />
      <div className="grid md:grid-cols-3 gap-4">
        {items.map((it, i) => (
          <motion.div
            key={it.testid}
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <BuiltForItem {...it} />
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

/* ───────────────────────── SECTION 8: PRICING ───────────────────────── */
export const PricingSection = ({ user }) => {
  const { lang } = useT();
  const tiers = [
    {
      name: 'Free', price: 0, sub: lang === 'fr' ? 'Découvre la magie' : 'Try the magic',
      features: lang === 'fr'
        ? ['1 vidéo de test', 'Export 720p', '1 piste de sous-titres', 'Filigrane HighlightAI']
        : ['1 test video', '720p export', '1 caption track', 'HighlightAI watermark'],
      cta: lang === 'fr' ? 'Commencer gratuitement' : 'Start free',
    },
    {
      name: 'Creator', price: 19, popular: true, sub: lang === 'fr' ? 'Pour créateurs ambitieux' : 'For ambitious creators',
      features: lang === 'fr'
        ? ['Vidéos illimitées', 'Export 1080p HD', 'Aucun filigrane', 'Traitement prioritaire', 'Publication sociale (bientôt)']
        : ['Unlimited videos', '1080p HD export', 'No watermark', 'Priority processing', 'Social publishing (soon)'],
      cta: lang === 'fr' ? 'Commencer gratuitement' : 'Start free',
    },
    {
      name: 'Studio', price: 49, sub: lang === 'fr' ? 'Équipes et agences' : 'Teams and agencies',
      features: lang === 'fr'
        ? ['Tout Creator', 'Export 4K', 'Comptes équipe', 'Branding custom', 'Accès API', 'Support dédié']
        : ['Everything in Creator', '4K export', 'Team accounts', 'Custom branding', 'API access', 'Dedicated support'],
      cta: lang === 'fr' ? 'Passer à Studio' : 'Go Studio',
    },
  ];
  return (
    <Section className="bg-[#070707]" testid="landing-pricing">
      <SectionHeader
        label={lang === 'fr' ? '// Tarifs' : '// Pricing'}
        title={lang === 'fr' ? 'Commence gratuit. Scale quand tu cartonnes.' : 'Start free. Scale when you blow up.'}
        subtitle={lang === 'fr' ? 'Aucune carte requise. Annule en un clic.' : 'No credit card required. Cancel anytime.'}
      />
      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {tiers.map((tier, i) => (
          <motion.div
            key={tier.name}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.5 }}
            className={`relative rounded-2xl p-8 ${tier.popular ? 'tier-popular lg:scale-105' : 'glass-card bento-card'}`}
            data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
          >
            {tier.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full acid-chip text-[10px] uppercase tracking-[0.2em] font-bold">
                {lang === 'fr' ? 'Le plus populaire' : 'Most popular'}
              </div>
            )}
            <h3 className="font-heading text-2xl font-black mb-1">{tier.name}</h3>
            <p className="text-sm text-zinc-500 mb-6">{tier.sub}</p>
            <div className="flex items-baseline gap-1 mb-8">
              <span className="font-heading text-5xl font-black tracking-tighter">{tier.price}€</span>
              <span className="text-zinc-500 text-sm">{lang === 'fr' ? '/ mois' : '/ month'}</span>
            </div>
            <ul className="space-y-3 mb-8">
              {tier.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                  <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.popular ? 'text-[#D4FF00]' : 'text-zinc-500'}`} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <Link to={user ? '/pricing' : '/register'}>
              <Button
                className={`w-full h-11 font-semibold ${tier.popular ? 'bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 glow-accent' : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'}`}
                data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
              >
                {tier.cta} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </motion.div>
        ))}
      </div>
    </Section>
  );
};

/* ───────────────────────── SECTION 9: FAQ ───────────────────────── */
const FAQItem = ({ q, a, idx, open, setOpen }) => (
  <div className="border-b border-white/10" data-testid={`faq-item-${idx + 1}`}>
    <button
      onClick={() => setOpen(open === idx ? null : idx)}
      className="w-full py-6 flex items-center justify-between gap-4 text-left hover:text-[#D4FF00] transition-colors"
      data-testid={`faq-toggle-${idx + 1}`}
    >
      <span className="font-heading text-base sm:text-lg font-bold">{q}</span>
      <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${open === idx ? 'rotate-180 text-[#D4FF00]' : 'text-zinc-500'}`} />
    </button>
    {open === idx && (
      <motion.div
        initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
        transition={{ duration: 0.2 }}
        className="pb-6 text-zinc-400 text-sm sm:text-base leading-relaxed"
      >
        {a}
      </motion.div>
    )}
  </div>
);

export const FAQ = () => {
  const { lang } = useT();
  const [open, setOpen] = useState(0);
  const faqs = lang === 'fr' ? [
    { q: 'Combien de temps prend le traitement ?', a: 'En moyenne moins de 5 minutes pour une vidéo source d\'1 heure. Le pipeline parallélise transcription Whisper, analyse GPT-5.2 et découpage FFmpeg.' },
    { q: 'Combien de clips sont générés par vidéo ?', a: 'Entre 5 et 10 clips selon la durée de ta source et la densité de moments viraux détectés par l\'IA.' },
    { q: 'Puis-je éditer les clips après génération ?', a: 'Oui, un éditeur intégré te permet d\'ajuster les timestamps, modifier les sous-titres et toucher au cadrage avant export.' },
    { q: 'Puis-je exporter sans sous-titres ?', a: 'Oui, chaque clip propose un toggle "sans sous-titres" pour récupérer la version brute sans burn-in.' },
    { q: 'Y a-t-il un plan gratuit ?', a: 'Oui — 1 vidéo de test gratuite, sans carte bancaire. Les exports gratuits portent un petit filigrane HighlightAI bas-droite.' },
    { q: 'Puis-je uploader des podcasts ?', a: 'Absolument. MP4, MOV, AVI, MKV et la plupart des formats audio/vidéo sont supportés, jusqu\'à 5 Go par fichier.' },
    { q: 'Puis-je uploader des streams Twitch ?', a: 'Oui — tout enregistrement Twitch téléchargé en .mp4 fonctionne. L\'IA gère parfaitement les longs streams (jusqu\'à 5 Go).' },
  ] : [
    { q: 'How long does processing take?', a: 'On average under 5 minutes for a 1-hour source video. The pipeline parallelizes Whisper transcription, GPT-5.2 analysis and FFmpeg cutting.' },
    { q: 'How many clips are generated per video?', a: 'Between 5 and 10 clips depending on your source length and density of viral moments detected by the AI.' },
    { q: 'Can I edit the clips?', a: 'Yes — a built-in editor lets you tweak timestamps, edit captions and adjust crops before export.' },
    { q: 'Can I remove subtitles?', a: 'Yes, each clip has a "no subtitles" toggle to grab the raw version without burned-in captions.' },
    { q: 'Is there a free plan?', a: 'Yes — 1 free test video, no credit card required. Free exports carry a small HighlightAI watermark in the bottom-right.' },
    { q: 'Can I upload podcasts?', a: 'Absolutely. MP4, MOV, AVI, MKV and most audio/video formats are supported, up to 5 GB per file.' },
    { q: 'Can I upload Twitch streams?', a: 'Yes — any Twitch recording exported as .mp4 works. The AI handles long streams perfectly (up to 5 GB).' },
  ];
  return (
    <Section testid="landing-faq">
      <SectionHeader
        label={lang === 'fr' ? '// FAQ' : '// FAQ'}
        title={lang === 'fr' ? 'Tout ce que tu veux savoir.' : 'Everything you want to know.'}
      />
      <div className="max-w-3xl mx-auto">
        {faqs.map((f, i) => <FAQItem key={i} idx={i} {...f} open={open} setOpen={setOpen} />)}
      </div>
    </Section>
  );
};

/* ───────────────────────── SECTION 10: FINAL CTA ───────────────────────── */
export const FinalCTA = ({ user }) => {
  const { lang } = useT();
  return (
    <Section className="relative overflow-hidden" testid="landing-final-cta">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[60%] h-[60%] rounded-full bg-[#D4FF00]/10 blur-[120px]" />
      </div>
      <div className="relative max-w-3xl mx-auto text-center">
        <motion.h2
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] mb-6"
        >
          {lang === 'fr' ? (
            <>Ton prochain clip viral<br />est déjà <span className="text-[#D4FF00]">dans ton podcast.</span></>
          ) : (
            <>Your next viral clip is<br />already <span className="text-[#D4FF00]">inside your podcast.</span></>
          )}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="text-zinc-400 text-lg mb-10"
        >
          {lang === 'fr' ? 'Aucune carte. Aucun engagement. Juste tes premiers clips, en 5 minutes.' : 'No credit card. No commitment. Just your first clips in 5 minutes.'}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <Link to={user ? '/upload' : '/register'}>
            <Button
              className="bg-[#D4FF00] text-black hover:bg-[#D4FF00]/90 font-semibold h-14 px-10 text-lg glow-accent"
              data-testid="final-cta-button"
            >
              {lang === 'fr' ? 'Générer mes premiers clips' : 'Generate my first clips'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </Section>
  );
};
